import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { Card, CardBody, Col } from "react-bootstrap";
import Select from "react-select";
import { ISpecialCondition } from "../../shared/interfaces/special-condition/ISpecialCondition";
import { SpecialConditionService } from "./service";
import { CustomPagination } from "../../shared/components/CustomPagination";
import { usePagination } from "../../shared/hooks/usePagination";
import DeleteModal from "../../Common/DeleteModal";

const PER_PAGE_DEFAULT = 20;

const SpecialConditionList = () => {
    const { t } = useTranslation();
    const [list, setList] = useState<ISpecialCondition[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [specialConditionToDelete, setSpecialConditionToDelete] = useState<number | null>(null);
    const {
        page,
        handlePages,
        pagination,
        totalPages,
        setTotalPages,
        changeFilterHandler,
        goToPreviousPage,
        goToNextPage,
    } = usePagination({
        perPage: PER_PAGE_DEFAULT,
    });
    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
    ];

    useEffect(() => {
        SpecialConditionService.getAll(pagination)
            .then((response) => {
                const data = response.data;
                setList(data);
                setTotalPages(response.totalPages);
            })
            .catch(ErrorToast);
    }, [pagination, setTotalPages]);

    const handleDeleteClick = (id: number) => {
        setSpecialConditionToDelete(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (specialConditionToDelete !== null) {
            SpecialConditionService.delete(specialConditionToDelete)
                .then(() => {
                    SuccessToast(t("specialCondition.deleteSuccess"));
                    setShowDeleteModal(false);
                    setSpecialConditionToDelete(null);
                    SpecialConditionService.getAll(pagination)
                        .then((response) => {
                            const data = response.data;
                            setList(data);
                            setTotalPages(response.totalPages);
                        })
                        .catch(ErrorToast);
                })
                .catch(ErrorToast);
        }
    };

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody>
                        <div className="text-end">
                            <button
                                className="btn btn-primary text-end"
                                onClick={() => navigate("/special-conditions/create")}
                            >
                                <i className="ti ti-plus f-18"></i> {t("specialCondition.add")}
                            </button>
                        </div>

                        <div className="d-sm-flex align-items-center mb-3">
                            <ul className="list-inline me-auto my-1">
                                <li className="list-inline-item">
                                    <Select
                                        placeholder={t("job.entriesPerPage")}
                                        options={entriesPerPageOptions}
                                        onChange={(selected) =>
                                            changeFilterHandler(
                                                "perPage",
                                                selected?.value ?? PER_PAGE_DEFAULT,
                                            )
                                        }
                                        defaultValue={entriesPerPageOptions.find(
                                            (option) => option.value === PER_PAGE_DEFAULT,
                                        )}
                                        classNamePrefix="react-select"
                                    />
                                </li>
                                <span> {t("job.entriesPerPage")}</span>
                            </ul>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-hover" id="pc-dt-simple-1">
                                <thead>
                                    <tr>
                                        <th>{t("specialCondition.id")}</th>
                                        <th>{t("specialCondition.name")}</th>
                                        <th>{t("specialCondition.createdAt")}</th>
                                        <th>{t("specialCondition.updatedAt")}</th>
                                        <th className="text-end">{t("specialCondition.actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((item: ISpecialCondition, index: number) => (
                                        <tr key={index}>
                                            <td>{item.id}</td>
                                            <td>{item.name}</td>
                                            <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}</td>
                                            <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '-'}</td>
                                            <td className="text-end">
                                                <ul className="list-inline mb-0">
                                                    <li
                                                        className="list-inline-item"
                                                        onClick={() =>
                                                            navigate(`/special-conditions/edit/${item.id}`)
                                                        }
                                                    >
                                                        <a
                                                            href="#"
                                                            className="avtar avtar-s btn-link-success btn-pc-default"
                                                        >
                                                            <i className="ti ti-edit f-20"></i>
                                                        </a>
                                                    </li>
                                                    <li className="list-inline-item">
                                                        <span
                                                            role="button"
                                                            className="avtar avtar-s btn-link-danger btn-pc-default"
                                                            onClick={() => item.id && handleDeleteClick(item.id)}
                                                        >
                                                            <i className="ti ti-trash f-20"></i>
                                                        </span>
                                                    </li>
                                                </ul>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <CustomPagination
                            page={page}
                            totalPages={totalPages}
                            handlePages={handlePages}
                            goToPreviousPage={goToPreviousPage}
                            goToNextPage={goToNextPage}
                        />
                    </CardBody>
                </Card>
            </Col>
            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name={t("specialCondition.specialCondition")}
            />
        </React.Fragment>
    );
};

export default SpecialConditionList;
